import { useState } from 'react';
import { useSearchParams, Navigate, Link } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import '@/styles/login.css';

const errorMessages: Record<string, string> = {
  domain_restricted:
    'Access is restricted to Zima Media team members only. Please sign in with your @zimamedia.com Google account.',
  auth_failed: 'Authentication failed. Please try again.',
  email_not_verified:
    'Your Google email address is not verified. Please verify your email and try again.',
};

const successMessages: Record<string, string> = {
  activation: 'Your account has been activated! Sign in to get started.',
};

type LoginStep = 'initial' | 'code_sent' | 'verifying';

export default function LoginPage() {
  const { user, loading, refresh } = useAuth();
  const [params] = useSearchParams();
  const error = params.get('error') || '';
  const activation = params.get('activation') || '';

  const [step, setStep] = useState<LoginStep>('initial');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [formError, setFormError] = useState('');
  const [formLoading, setFormLoading] = useState(false);
  const [codeSentMessage, setCodeSentMessage] = useState('');

  if (loading) {
    return (
      <div className="login-loading">
        <span>Loading...</span>
      </div>
    );
  }

  if (user) {
    if (user.plan === 'waitlist') {
      return <Navigate to="/waitlist" replace />;
    }
    return <Navigate to="/chat" replace />;
  }

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    setFormLoading(true);
    setFormError('');

    try {
      const res = await fetch('/auth/email-login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });
      const data = await res.json();

      if (!res.ok) {
        if (res.status === 404) {
          setFormError('no_account');
        } else {
          setFormError(data.error || 'Something went wrong.');
        }
        return;
      }

      setCodeSentMessage(data.message);
      setStep('code_sent');
    } catch {
      setFormError('Network error. Please try again.');
    } finally {
      setFormLoading(false);
    }
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!code.trim()) return;

    setFormLoading(true);
    setFormError('');
    setStep('verifying');

    try {
      const res = await fetch('/auth/verify-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), code: code.trim() }),
      });
      const data = await res.json();

      if (!res.ok) {
        setFormError(data.error || 'Invalid code.');
        setStep('code_sent');
        return;
      }

      await refresh();
      window.location.href = data.redirect || '/chat';
    } catch {
      setFormError('Network error. Please try again.');
      setStep('code_sent');
    } finally {
      setFormLoading(false);
    }
  };

  const handleResendCode = async () => {
    setCode('');
    setFormError('');
    setStep('initial');
    setCodeSentMessage('');
  };

  return (
    <div className="login-page">
      <div className="hero-glow login-glow" />

      <div className="login-card">
        <div className="login-icon">
          <img src="/Final-white.png" alt="Zima Media" className="login-logo-img" />
        </div>

        <h1>Google Ads Assistant</h1>
        <p className="login-subtitle">
          Sign in with your Zima Media account to access the Google Ads AI assistant.
        </p>

        {error && errorMessages[error] && (
          <div className="login-error">{errorMessages[error]}</div>
        )}

        {activation === 'success' && (
          <div className="login-success">{successMessages.activation}</div>
        )}

        {formError && formError !== 'no_account' && <div className="login-error">{formError}</div>}

        {formError === 'no_account' && (
          <div className="login-error">
            No account found with this email. <Link to="/#hero-waitlist">Join the waitlist</Link> to get started.
          </div>
        )}

        {step === 'initial' && (
          <>
            <a href="/auth/login" className="login-google-btn">
              <svg width="20" height="20" viewBox="0 0 48 48">
                <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
                <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
                <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
                <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
              </svg>
              Sign in with Google
            </a>

            <div className="login-divider">
              <span>or</span>
            </div>

            <form onSubmit={handleSendCode} className="login-email-form">
              <input
                type="email"
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="login-input"
                required
                autoComplete="email"
              />
              <button type="submit" className="login-email-btn" disabled={formLoading || !email.trim()}>
                {formLoading ? 'Sending...' : 'Send Login Code'}
              </button>
            </form>
          </>
        )}

        {(step === 'code_sent' || step === 'verifying') && (
          <div className="login-code-section">
            <div className="login-code-sent-msg">
              <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="#22c55e" strokeWidth="2">
                <path d="M22 2L11 13" /><path d="M22 2L15 22L11 13L2 9L22 2Z" />
              </svg>
              <span>{codeSentMessage || `Code sent to ${email}`}</span>
            </div>

            <form onSubmit={handleVerifyCode} className="login-email-form">
              <input
                type="text"
                placeholder="Enter 6-digit code"
                value={code}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setCode(val);
                }}
                className="login-input login-code-input"
                maxLength={6}
                autoComplete="one-time-code"
                inputMode="numeric"
                autoFocus
              />
              <button type="submit" className="login-email-btn" disabled={formLoading || code.length !== 6}>
                {formLoading ? 'Verifying...' : 'Verify Code'}
              </button>
            </form>

            <button onClick={handleResendCode} className="login-resend-btn" type="button">
              Use a different email or resend code
            </button>
          </div>
        )}

        <p className="login-footer-text">Beta Only Approved Accounts Can Login</p>
      </div>
    </div>
  );
}
